import React, { useState, useEffect, useRef } from "react";
import { Modal } from "../CommonUIComponents/Modal/Modal";
import { Button } from "../CommonUIComponents/Button/Button";
import { EditableDelegateField } from "../CommonUIComponents/EditableDelegateField/EditableDelegateField";
import { MotifDatePicker } from "@ey-xd/motif-react";
import { useI18n } from "../../hooks/useI18n";
import { utcToLocalDateInput } from "../../util/dateUtil";
import "./EditServiceDetailsModal.scss";

const MAX_DELEGATES = 5;

// Normalize any incoming date value (UTC ISO date-time or yyyy-mm-dd) into a
// local-calendar yyyy-mm-dd string for the date picker. UTC date-times are
// shifted into the user's local timezone.
function formatToISO(v) {
  return utcToLocalDateInput(v);
}

function normalizeDateValue(val) {
  const toLocal = (d) => {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };
  if (Array.isArray(val)) {
    const first = val[0];
    return first ? toLocal(new Date(first)) : "";
  }
  if (val instanceof Date) return toLocal(val);
  if (typeof val === "string") {
    const match = val.match(/^(\d{4}-\d{2}-\d{2})/);
    return match ? match[1] : val.slice(0, 10);
  }
  return "";
}

export function EditServiceDetailsModal({
  show,
  onClose,
  onSave,
  data = {},
  currentStageId,
  isCurrentUserDelegate = false,
  loading = false,
  error = "",
  onClearError,
}) {
  const { t } = useI18n();
  const [revisedStartDate, setRevisedStartDate] = useState("");
  const [revisedEndDate, setRevisedEndDate] = useState("");
  const [delegates, setDelegates] = useState([]);
  const initialValues = useRef({});

  useEffect(() => {
    if (show) {
      const startDate = formatToISO(data.revisedStartDate);
      const endDate = formatToISO(data.revisedEndDate);
      const delegateList = (data.engagementDelegates || [])
        .filter((d) => d.delegateEmail || d.emailAddress)
        .map((d) => {
          const nameParts = (d.delegateName || "").trim().split(/\s+/);
          return {
            emailAddress: d.delegateEmail || d.emailAddress,
            firstName: d.firstName || nameParts[0] || "",
            lastName: d.lastName || nameParts.slice(1).join(" ") || "",
          };
        });

      setRevisedStartDate(startDate);
      setRevisedEndDate(endDate);
      setDelegates(delegateList);

      initialValues.current = {
        revisedStartDate: startDate,
        revisedEndDate: endDate,
        delegateEmails: delegateList
          .map((d) => d.emailAddress)
          .sort()
          .join(","),
      };
    }
  }, [
    show,
    data.revisedStartDate,
    data.revisedEndDate,
    data.engagementDelegates,
  ]);

  const delegatesChanged =
    delegates
      .map((d) => d.emailAddress)
      .sort()
      .join(",") !== initialValues.current.delegateEmails;

  const dirty = {
    revisedStartDate:
      revisedStartDate !== initialValues.current.revisedStartDate,
    revisedEndDate: revisedEndDate !== initialValues.current.revisedEndDate,
    delegates: delegatesChanged,
  };

  const hasChanges = Object.values(dirty).some(Boolean);
  const shouldValidateRevisedDateRange =
    dirty.revisedStartDate || dirty.revisedEndDate;
  const hasInvalidRevisedDateRange =
    shouldValidateRevisedDateRange &&
    Boolean(revisedStartDate) &&
    Boolean(revisedEndDate) &&
    revisedStartDate > revisedEndDate;
  const revisedDateValidationError = hasInvalidRevisedDateRange
    ? t("service_tab_revisedDateRangeValidation", {
        defaultValue:
          "Revised Start Date must be less than or equal to Revised End Date.",
      })
    : "";

  const hasTooManyDelegates = delegates.length > MAX_DELEGATES;
  const delegatesValidationError = hasTooManyDelegates
    ? t("service_tab_delegatesMaxValidation", {
        defaultValue: `You can add a maximum of ${MAX_DELEGATES} Engagement Delegates.`,
        max: MAX_DELEGATES,
      })
    : "";

  const canEditStartDate = currentStageId == null || currentStageId <= 5;
  const canEditEndDate = currentStageId == null || currentStageId <= 6;
  const canEditDelegates = !isCurrentUserDelegate && (currentStageId == null || currentStageId <= 8);

  const handleSave = () => {
    // Emit only dirty fields so the parent can build a minimal payload.
    const changes = {};
    if (dirty.revisedStartDate) changes.revisedStartDate = revisedStartDate;
    if (dirty.revisedEndDate) changes.revisedEndDate = revisedEndDate;
    if (dirty.delegates) changes.delegates = delegates;
    onSave(changes);
  };

  const body = (
    <div className="edit-service-details-body">
      <div className="edit-service-details-row">
        <div className="edit-service-details-field">
          <label className="field-label motif-body2-default-medium">
            {t("service_tab_revisedStartDate")}
          </label>
          <MotifDatePicker
            className={`edit-modal-date${!revisedStartDate ? " motif-date-empty" : ""}`}
            format="dd/MM/y"
            value={revisedStartDate || ""}
            disabled={!canEditStartDate}
            onChange={(val) => {
              onClearError?.();
              setRevisedStartDate(normalizeDateValue(val));
            }}
          />
        </div>

        <div className="edit-service-details-field">
          <label className="field-label motif-body2-default-medium">
            {t("service_tab_revisedEndDate")}
          </label>
          <MotifDatePicker
            className={`edit-modal-date${!revisedEndDate ? " motif-date-empty" : ""}`}
            format="dd/MM/y"
            value={revisedEndDate || ""}
            disabled={!canEditEndDate}
            onChange={(val) => {
              onClearError?.();
              setRevisedEndDate(normalizeDateValue(val));
            }}
          />
        </div>
      </div>

      <div className="edit-service-details-field edit-service-details-field--delegates">
        <label className="field-label motif-body2-default-medium">
          {t("service_tab_engagementDelegates")}
        </label>
        <EditableDelegateField
          delegates={delegates.map((d) => ({
            delegateEmail: d.emailAddress,
            firstName: d.firstName,
            lastName: d.lastName,
          }))}
          onChange={(value) => {
            onClearError?.();
            setDelegates(value);
          }}
          disabled={!canEditDelegates}
          maxItems={MAX_DELEGATES}
          limitReachedMessage={t("service_tab_delegatesMaxValidation", {
            defaultValue: `You can add a maximum of ${MAX_DELEGATES} Engagement Delegates.`,
            max: MAX_DELEGATES,
          })}
        />
      </div>
      {(revisedDateValidationError || delegatesValidationError || error) && (
        <div className="edit-service-details-error" role="alert">
          {revisedDateValidationError || delegatesValidationError || error}
        </div>
      )}
    </div>
  );

  const footer = (
    <div className="edit-service-details-footer">
      <Button variant="secondary" onClick={onClose}>
        {t("cancel")}
      </Button>
      <Button
        variant="primary"
        onClick={handleSave}
        disabled={
          loading ||
          !hasChanges ||
          hasInvalidRevisedDateRange ||
          hasTooManyDelegates
        }
      >
        {loading ? t("user_mng_saving") : t("save")}
      </Button>
    </div>
  );

  return (
    <Modal
      show={show}
      onClose={onClose}
      header={t("view_order_page_editServiceDetails")}
      body={body}
      footer={footer}
      size="xl"
      className="edit-service-details-modal"
    />
  );
}
